import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import {
    DEFAULT_DATE_RANGE,
    GLOBAL_FILTER_FIELDS,
    emptyGlobalFilterSnapshot,
    isGlobalFilterField,
    type DateRangePreset,
    type GlobalDateRange,
    type GlobalFilterField,
    type GlobalFilterSnapshot,
} from './GlobalFilterTypes';

const RANGE_PARAM = 'range';

/**
 * URL query-param name for each global dimension. Kept short and readable
 * (`?provider=…&user=…`) in the CloudSaver 3.0 style rather than a generic
 * opaque blob.
 */
const FIELD_PARAM: Record<GlobalFilterField, string> = {
    provider_connection_id: 'provider',
    user_id: 'user',
};

const VALID_PRESETS: DateRangePreset[] = ['last-7', 'last-30', 'last-90', 'last-12mo', 'mtd'];
const RANGE_TOKEN_RE = /^\d{8}-\d{8}$/;
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The query-param keys this codec owns, so callers can preserve everything else. */
export const GLOBAL_FILTER_PARAM_KEYS: string[] = [RANGE_PARAM, ...GLOBAL_FILTER_FIELDS.map((field) => FIELD_PARAM[field])];

/** Reads a global filter snapshot out of URL query params, tolerating malformed values. */
export function decodeGlobalFilters(params: URLSearchParams): GlobalFilterSnapshot {
    const snapshot = emptyGlobalFilterSnapshot();
    const rawRange = params.get(RANGE_PARAM);
    if (rawRange !== null) snapshot.dateRange = decodeDateRange(rawRange);

    const dimensions: AnalyticsFilter[] = [];
    for (const field of GLOBAL_FILTER_FIELDS) {
        const raw = params.get(FIELD_PARAM[field]);
        if (raw === null) continue;
        const values = raw
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        if (values.length > 0) dimensions.push({ field, values });
    }
    snapshot.dimensions = dimensions;
    return snapshot;
}

/**
 * Writes a snapshot into a copy of `base`, touching only the params this codec
 * owns — unrelated params (e.g. a page's active tab) are preserved.
 */
export function encodeGlobalFilters(snapshot: GlobalFilterSnapshot, base: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(base);
    for (const key of GLOBAL_FILTER_PARAM_KEYS) next.delete(key);

    const encodedRange = encodeDateRange(snapshot.dateRange);
    if (encodedRange !== null) next.set(RANGE_PARAM, encodedRange);

    for (const filter of snapshot.dimensions) {
        if (!isGlobalFilterField(filter.field)) continue;
        if (filter.values.length === 0) continue;
        next.set(FIELD_PARAM[filter.field], filter.values.join(','));
    }
    return next;
}

function decodeDateRange(raw: string): GlobalDateRange {
    if ((VALID_PRESETS as string[]).includes(raw)) {
        return { kind: 'preset', preset: raw as DateRangePreset, start: null, end: null };
    }
    if (RANGE_TOKEN_RE.test(raw)) {
        const [from = '', to = ''] = raw.split('-');
        return { kind: 'custom', preset: null, start: expandCompactDay(from), end: expandCompactDay(to) };
    }
    return { ...DEFAULT_DATE_RANGE };
}

function encodeDateRange(range: GlobalDateRange): string | null {
    if (range.kind === 'custom' && range.start !== null && range.end !== null && ISO_DAY_RE.test(range.start) && ISO_DAY_RE.test(range.end)) {
        return `${compactDay(range.start)}-${compactDay(range.end)}`;
    }
    const preset = range.preset ?? DEFAULT_DATE_RANGE.preset;
    if (preset === null) return null;
    if (preset === DEFAULT_DATE_RANGE.preset) return null;
    return preset;
}

function compactDay(isoDay: string): string {
    return isoDay.replace(/-/g, '');
}

function expandCompactDay(compact: string): string {
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
}
